import isNil from 'lodash.isnil';
import omitBy from 'lodash.omitby';

// Google rejects an empty or blank User-ID and the words "null"/"NULL". "undefined" and "NaN" are what String() makes of a missing id.
const INVALID_USER_IDS = ['', 'null', 'undefined', 'nan'];

// Google's sign-out example sends the User-ID as null, which clears the one an earlier config call set
const SIGNED_OUT_USER_ID_PARAMS = {
  user_id: null,
  userId: null,
  app_user_id: null,
};

export interface IGetGtagConfigParams {
  hasSignedOut?: boolean;
  isDebugMode: boolean;
  userId?: unknown;
}

/**
 * Returns the id as a string when it can be sent to Google Analytics as a User-ID, otherwise undefined.
 */
export const getGoogleAnalyticsUserId = (id: unknown): string | undefined => {
  if (typeof id === 'number') {
    return Number.isSafeInteger(id) && id > 0 ? String(id) : undefined;
  }

  if (typeof id !== 'string') {
    return undefined;
  }

  const trimmedId = id.trim();

  return INVALID_USER_IDS.includes(trimmedId.toLowerCase()) ? undefined : trimmedId;
};

/**
 * Builds the gtag('config', ...) parameters. user_id is the GA4 User-ID. userId and app_user_id are custom parameters for GA
 * custom definitions (GIS-2699), the same as the shared useGoogleAnalytics hook sends. All three are left out when there is no
 * valid user id, so GA never gets an empty User-ID, or sent as null once a user who had one has signed out on this page.
 */
export const getGtagConfigParams = ({ hasSignedOut = false, isDebugMode, userId }: IGetGtagConfigParams) => {
  const googleAnalyticsUserId = getGoogleAnalyticsUserId(userId);

  const params = omitBy(
    {
      debug_mode: isDebugMode ? true : undefined,
      send_page_view: false,
      user_id: googleAnalyticsUserId,
      userId: googleAnalyticsUserId,
      app_user_id: googleAnalyticsUserId,
    },
    isNil,
  );

  return hasSignedOut && !googleAnalyticsUserId ? { ...params, ...SIGNED_OUT_USER_ID_PARAMS } : params;
};
